import React from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  ArrowForwardOutlined,
  EditOutlined,
  ExitToApp,
  ManageAccountsOutlined,
  SecurityOutlined,
  VerifiedUserOutlined,
  VpnKeyOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { ROUTES } from '../routes';

function getAppBaseUrl(apps = [], key = 'itam') {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
      if (key === 'helpdesk') {
        return (process.env.REACT_APP_HELPDESK_URL || 'http://localhost:3002').replace(/\/+$/, '');
      }
      return (process.env.REACT_APP_ITAM_URL || 'http://localhost:3001').replace(/\/+$/, '');
    }
  }

  const appList = Array.isArray(apps) ? apps : [];
  const appUrl = appList.find((app) => app?.key === key)?.url;
  if (appUrl) return appUrl;

  if (key === 'helpdesk') {
    return (process.env.REACT_APP_HELPDESK_URL || 'https://helpdesk.sushaservices.com').replace(/\/+$/, '');
  }
  return (process.env.REACT_APP_ITAM_URL || 'https://itam.sushaservices.com').replace(/\/+$/, '');
}

function buildAppHandoffUrl(apps, key, token, user, enterprise, memberships = [], returnTo = '/enterprise') {
  const baseUrl = getAppBaseUrl(apps, key);
  const sessionPayload = {
    token: token || null,
    selected_app: key,
    user: user || null,
    enterprise: enterprise || null,
    role: user?.role || null,
    memberships: Array.isArray(memberships) ? memberships : [],
    page_access: Array.isArray(user?.page_access) ? user.page_access : [],
    apps,
  };
  const params = new URLSearchParams({
    token: token || '',
    role: user?.role || '',
    enterprise_id: enterprise?.id ? String(enterprise.id) : '',
    app: key,
    returnTo,
    session: JSON.stringify(sessionPayload),
  });
  return `${baseUrl.replace(/\/+$/, '')}/oauth/callback#${params.toString()}`;
}

function formatRoleLabel(role) {
  return String(role || 'Member')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export default function AuthShell({
  title,
  subtitle,
  sideTitle,
  sideBody,
  children,
  topBrandLabel = 'Susha Services',
  topBrandIcon = true,
  topBrandImageSrc = null,
  topBrandImageAlt = 'Susha Services logo',
  showWorkspaceSwitchers = true,
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const apps = Array.isArray(auth.apps) ? auth.apps : [];
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const userName = auth?.user?.full_name || auth?.user?.name || 'User';
  const roleLabel = formatRoleLabel(auth?.role || auth?.user?.role);

  const handleLogout = React.useCallback(() => {
    setAnchorEl(null);
    navigate('/logout', { replace: true });
  }, [navigate]);

  const openChangePassword = React.useCallback(() => {
    setAnchorEl(null);
    navigate(ROUTES.CHANGE_PASSWORD);
  }, [navigate]);

  const openRoleManagement = React.useCallback(() => {
    setAnchorEl(null);
    if ((auth?.role || auth?.user?.role) !== 'enterprise_admin') {
      return;
    }
    navigate(ROUTES.USER_ROLE_MANAGEMENT);
  }, [navigate]);

  const openEnterpriseEdit = React.useCallback(() => {
    setAnchorEl(null);
    if (!auth.enterprise?.id && !auth.enterprise?.name) {
      return;
    }
    window.location.assign(
      buildAppHandoffUrl(apps, 'itam', auth.token, auth.user, auth.enterprise, auth.memberships, '/enterprise/profile')
    );
  }, [apps, auth.enterprise, auth.memberships, auth.token, auth.user]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: 2,
        py: { xs: 2, md: 3 },
        background:
          'radial-gradient(circle at top left, rgba(11,78,162,0.12) 0%, rgba(11,78,162,0) 35%), radial-gradient(circle at bottom right, rgba(15,118,110,0.10) 0%, rgba(15,118,110,0) 32%), #f4f7fb',
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #e2e8f0',
          color: '#0f172a',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 60, sm: 72 }, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {topBrandImageSrc ? (
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  bgcolor: '#ffffff',
                  border: '1px solid #e2e8f0',
                }}
              >
                <Box
                  component="img"
                  src={topBrandImageSrc}
                  alt={topBrandImageAlt}
                  sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.25 }}
                />
              </Box>
            ) : topBrandIcon ? (
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: '#dbeafe',
                  color: '#0b4ea2',
                }}
              >
                <SecurityOutlined />
              </Box>
            ) : null}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {topBrandLabel}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {auth?.token ? (
            <Stack direction="row" spacing={1} alignItems="center">
              {showWorkspaceSwitchers ? (
                <Stack direction="row" spacing={1} sx={{ mr: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ArrowForwardOutlined fontSize="small" />}
                    onClick={() => {
                      window.location.assign(
                        buildAppHandoffUrl(apps, 'itam', auth.token, auth.user, auth.enterprise, auth.memberships)
                      );
                    }}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
                  >
                    ITAM
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ArrowForwardOutlined fontSize="small" />}
                    onClick={() => {
                      window.location.assign(
                        buildAppHandoffUrl(apps, 'helpdesk', auth.token, auth.user, auth.enterprise, auth.memberships)
                      );
                    }}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
                  >
                    Helpdesk
                  </Button>
                </Stack>
              ) : null}

              <Box
                sx={{
                  textAlign: 'right',
                  mr: 0.5,
                  minWidth: 0,
                  display: { xs: 'none', sm: 'block' },
                  maxWidth: 280,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="#1e293b"
                  sx={{
                    lineHeight: 1.2,
                    fontSize: '0.88rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {auth.enterprise?.name || 'Enterprise'}
                </Typography>
                <Typography
                  variant="caption"
                  color="#64748b"
                  sx={{
                    display: 'block',
                    lineHeight: 1.2,
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {roleLabel}
                </Typography>
              </Box>

              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: '#3b82f6',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                  }}
                >
                  {String(userName || 'U').charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 280,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                  },
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    bgcolor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: '#0f172a',
                      mb: 0.5,
                      fontSize: '0.95rem',
                    }}
                  >
                    {userName}
                  </Typography>
                  <Chip
                    label={roleLabel}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      bgcolor: '#e0f2fe',
                      color: '#075985',
                      '& .MuiChip-label': { px: 1.5 },
                    }}
                  />
                </Box>

                {auth.enterprise?.id || auth.enterprise?.name ? (
                  <MenuItem
                    onClick={openEnterpriseEdit}
                    sx={{ py: 1.5, px: 2.5, fontSize: '0.9rem' }}
                  >
                    <EditOutlined fontSize="small" sx={{ mr: 2, color: '#64748b' }} />
                    Edit Enterprise detail
                  </MenuItem>
                ) : null}
                <MenuItem
                  onClick={openChangePassword}
                  sx={{ py: 1.5, px: 2.5, fontSize: '0.9rem' }}
                >
                  <VpnKeyOutlined fontSize="small" sx={{ mr: 2, color: '#64748b' }} />
                  Change Password
                </MenuItem>
                {(auth?.role || auth?.user?.role) === 'enterprise_admin' ? (
                  <MenuItem
                    onClick={openRoleManagement}
                    sx={{ py: 1.5, px: 2.5, fontSize: '0.9rem' }}
                  >
                    <ManageAccountsOutlined fontSize="small" sx={{ mr: 2, color: '#64748b' }} />
                    User & Role Management
                  </MenuItem>
                ) : null}
                <Divider />
                <MenuItem
                  onClick={handleLogout}
                  sx={{ py: 1.5, px: 2.5, fontSize: '0.9rem' }}
                >
                  <ExitToApp fontSize="small" sx={{ mr: 2, color: '#64748b' }} />
                  Logout
                </MenuItem>
              </Menu>
            </Stack>
          ) : null}
        </Toolbar>
      </AppBar>

      <Box sx={{ pt: { xs: 10, sm: 12 } }}>
        <Paper
          elevation={0}
          sx={{
            maxWidth: 1120,
            width: '100%',
            mx: 'auto',
            overflow: 'hidden',
            border: '1px solid #dbe7f5',
            backgroundColor: '#fff',
            borderRadius: 4,
          }}
        >
          <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            {(sideTitle || sideBody) && (
              <Stack spacing={1.25} sx={{ mb: 3 }}>
                <Chip
                  icon={<VerifiedUserOutlined />}
                  label="Shared identity for ITAM and Helpdesk"
                  sx={{
                    alignSelf: 'flex-start',
                    bgcolor: '#dbeafe',
                    color: '#0b4ea2',
                    fontWeight: 800,
                  }}
                />
                {sideTitle ? (
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    {sideTitle}
                  </Typography>
                ) : null}
                {sideBody ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {sideBody}
                  </Typography>
                ) : null}
              </Stack>
            )}

            <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, mb: 0.8 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              {subtitle}
            </Typography>
            {children}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
